import { X } from "lucide-react";
import { Chip, type ChipTone } from "@/components/ui/chip";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { formatRelativeTime } from "@/lib/freshness";
import type { OpenWorkRequestRow } from "@iamu/db";

const KIND_LABEL: Record<OpenWorkRequestRow["kind"], string> = {
  fix: "Fix",
  verify: "Verify",
  run: "Run",
};
const STALE_MS = 24 * 3600_000;

export function describeRequest(r: OpenWorkRequestRow): {
  label: string;
  tone: ChipTone;
  hint: string | null;
} {
  const kind = KIND_LABEL[r.kind];
  if (r.executor === "cloud") {
    if (r.walkthroughStatus === "failed") {
      return {
        label: "Cloud run failed",
        tone: "critical",
        hint: "Re-queue to try again",
      };
    }
    if (r.walkthroughStatus === "running") {
      return {
        label: `${kind} running in cloud`,
        tone: "primary",
        hint: null,
      };
    }
    return {
      label: `${kind} queued for cloud`,
      tone: "primary",
      hint: "Waiting for a runner",
    };
  }
  if (r.status === "claimed") {
    return {
      label: `${kind} claimed · ${r.claimedBy ?? "an agent"}`,
      tone: "primary",
      hint: null,
    };
  }
  const stale = Date.now() - new Date(r.requestedAt).getTime() > STALE_MS;
  return {
    label: `${kind} queued`,
    tone: stale ? "important" : "outline",
    hint: stale
      ? "not picked up — the team's CLI may predate work requests"
      : null,
  };
}

export function WorkRequestChip({
  request,
  onCancel,
}: {
  request: OpenWorkRequestRow;
  onCancel?: () => void;
}) {
  const { label, tone, hint } = describeRequest(request);
  const age = formatRelativeTime(request.requestedAt);
  const requestedBy = `Requested by ${request.requestedBy}`;
  return (
    <span className="inline-flex items-center gap-1.5">
      <SimpleTooltip content={requestedBy}>
        <Chip
          tone={tone}
          tabIndex={0}
          className="cursor-help focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {label}
        </Chip>
      </SimpleTooltip>
      <span className="text-xs tabular-nums text-muted-foreground">{age}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      {onCancel && (
        <SimpleTooltip content="Cancel request">
          <button
            type="button"
            aria-label="Cancel request"
            className="text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
          >
            <X className="size-3" />
          </button>
        </SimpleTooltip>
      )}
    </span>
  );
}
